// 🌟 Imports
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../contexts/ThemeContext";
import 'suneditor/dist/css/suneditor.min.css';
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Pagination,Autoplay } from "swiper";
import "swiper/swiper.min.css";
import { BUILDER_URL, setImageSrc } from '../utils/config';
import * as Sentry from "@sentry/react";
import Custom404 from '../pages/404';
import getRedisClient from '../utils/redis';
import {NoDataFound} from '../pages/404';
import getRedisClientLocal from '../utils/localredis';
import errorLogApi from '../components/custom/ErrorLogApi';
import useTracking from '../components/custom/useTracking';

// 💡 Swiper Configuration
SwiperCore.use([Navigation, Pagination, Autoplay]);
// 🌍 Constants
const BASE_URL = BUILDER_URL;

function About() {
  // 🎨 Theme and Data States
  const theme = useContext(ThemeContext); 
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorState, setErrorState] = useState(false);
  const trackPage = useTracking('About Us Page');

    // 🔄 Fetching Data on Component Mount
  useEffect(() => {
    
      axios.get(`${BASE_URL}/about-us-page`, {
          headers: { 'Authorization': `Bearer ${theme.token}` }
        })
        .then((response) => {
          if (response.data && response.data.length > 0) {
            setData(response.data[0]);
            setIsLoading(false);
          }
          setErrorState(false);
        })
        .catch((error) => {
          setIsLoading(false);
          Sentry.captureException(error);
          setErrorState(true);
        });
        trackPage();
  }, [theme]);

  // ✨ Shimmer Loader
  if (isLoading) {
    return (
        <div className="block xl:container mx-auto px-4 py-12 bg-gray-50 min-h-screen font-body">
            {/* Title & Description with Shimmer */}
            <div className="text-center py-8">
            <div className="h-10 w-1/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-8 rounded"></div>
                <div className="h-6 w-3/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-4 rounded"></div>
            </div>

            {/* Our Values with Shimmer */}
            <div className="mb-16">
                <div className="h-10 w-1/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-8 rounded"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {[...Array(2)].map((_, idx) => (
                        <div key={idx} className="flex flex-col items-center text-center p-8 border border-gray-200 bg-white shadow-lg rounded-lg">
                            <div className="h-16 w-16 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mb-4 rounded-full"></div>
                            <div className="h-6 w-3/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mb-2 rounded"></div>
                            <div className="h-4 w-5/6 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer rounded"></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Our Team with Shimmer */}
            <div className="mb-16">
                <div className="h-10 w-1/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-8 rounded"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                    {[...Array(3)].map((_, idx) => (
                        <div key={idx} className="text-center p-8 border border-gray-200 bg-white shadow-lg rounded-lg">
                            <div className="mx-auto rounded-full w-32 h-32 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mb-4"></div>
                            <div className="h-6 w-2/3 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-2 rounded"></div>
                            <div className="h-4 w-1/2 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto rounded"></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Our History with Shimmer */}
            <div className="bg-alternative text-white rounded-lg p-4 lg:p-12 mb-16 shadow-lg">
                <div className="h-8 w-2/5 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-6 rounded"></div>
                <div className="h-6 w-4/5 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-4 rounded"></div>
                <div className="h-6 w-3/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto rounded"></div>
            </div>
        </div>
    );
}
const ReadMore = ({ content, maxLength }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleIsExpanded = () => setIsExpanded(!isExpanded);
  
    return (
      <div className="relative">
        <div
          className={`prose overflow-auto ${isExpanded ? 'h-56' : 'h-56'}`}
          dangerouslySetInnerHTML={{
            __html: isExpanded ? content : content.substr(0, maxLength) ,
          }}
        />
        {(content.length > maxLength) && (
          <button
            onClick={toggleIsExpanded}
            className="text-blue-600 hover:text-blue-800 hover:underline mt-2"
            aria-expanded={isExpanded}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </button>
        )}
      </div>
    );
  };

  if(errorState) {
    return (
      <>
        <Custom404 />
      </>
    )
  }

  return (
    <>
    {data?<div className="block xl:container mx-auto px-4 py-12 bg-gray-50 min-h-screen font-body">
          <div className="text-center py-8">
              <h1 className="text-5xl font-bold text-blue-800 mb-4 font-display">{data?.title}</h1>
              <p className="text-lg text-gray-600" dangerouslySetInnerHTML={{ __html: data?.about_us_description }}></p>
          </div>

          {data?.is_info_management? (
              <div className="mb-16">
                  <h2 className="text-4xl font-bold text-blue-800 mb-2 lg:mb-10 text-center font-display">Our Values</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      {data.info_management.map((value) => (
                          <div key={value.info_management_id} className="flex flex-col items-center text-center p-8 border border-gray-200 bg-white shadow-lg rounded-lg transform hover:scale-105 hover:shadow-2xl transition-all duration-500">
                              <img className="h-16 w-16 mb-4" src={setImageSrc(value.icon_image, '')} alt={value.info_title} />
                              <h3 className="text-2xl font-semibold mb-2">{value.info_title}</h3>
                              <ReadMore content={value.info_description} maxLength={1500} />
                          </div>
                      ))}
                  </div>
              </div>
          ):false}

          {data?.is_profile_management ? (
              <div className="mb-16">
                <h2 className="text-4xl font-bold text-blue-800 mb-2 lg:mb-10  text-center font-display">Our Team</h2>
                  <Swiper
          className="custom-swiper"
          navigation={{
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          }}
          autoplay={{ delay: 2500, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          
          breakpoints={{
            320: { slidesPerView: 1 }, // Show 1 slide in very small screens
            480: { slidesPerView: 1 }, // Show 2 slides in small screens
            640: { slidesPerView: 2 }, // Show 3 slides in medium screens
            768: { slidesPerView: 2 }, // Show 4 slides in large screens
            1024: { slidesPerView: 3 }, // Show 5 slides in extra large screens
            1440: { slidesPerView: 3 }, // Show 5 slides in extra large screens
          }}
        >


                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                      {data?.profile_management.map((member,index) => (
                          <SwiperSlide key={index} className="flex justify-center gap-2 p-2">

                          <div key={member.profile_management_id} className="text-center p-8 border border-gray-200 bg-white shadow-lg rounded-lg transform hover:scale-105 hover:shadow-2xl transition-all duration-500">
                              <img className="mx-auto rounded-full w-32 h-32 mb-4 border-2 border-blue-200" src={setImageSrc(member.profile_image,'')} alt={member.profile_name} />
                              <h3 className="text-2xl font-semibold mb-2">{member.profile_name}</h3>
                              <p className="text-lg text-gray-600">{member.designation}</p>
                          </div>
                          </SwiperSlide>
                      ))}
                  </div>

        </Swiper>
                  
              </div>
          ):false}

          {data?.is_history ? (
              <div className="bg-alternative text-white rounded-lg p-4 lg:p-12 mb-16 shadow-lg">
                  <h2 className="text-2xl lg:text-4xl  font-bold mb-2 lg:mb-6 text-center font-display">Our History</h2>
                  <p className="text-lg text-center" dangerouslySetInnerHTML={{ __html: data.history_description }}></p>
              </div>
          ):false}
      </div>:<NoDataFound/>}
      
    </>
  );
}

export async function getServerSideProps(context) {
  const { req } = context;
  // Helper function to extract the domain name
  function sanitizeHostname(host) {
    const hostname = new URL(`https://${host}`).hostname;
    return hostname;
  }

  const logError = (endpoint, error) => {
    const responseData = error.response?.data ?? '';
    errorLogApi({
      job_id: "",
      endpoint: endpoint,
      payload: '',
      responseData: responseData,
      errorMsg: error
    });
  };

  
  const hostname = req.headers.host === "localhost:3000" ? process.env.NEXT_PUBLIC_WEB_HOST : sanitizeHostname(req.headers.host);

  const redishostname = sanitizeHostname(req.headers.host);
  const redis = redishostname.startsWith('localhost') ? getRedisClientLocal() : getRedisClient();
  

  const previewUrl = req.headers.host === "localhost:3000"
    ? "https://preview.jpencil.in/?pid=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqYm9faWQiOjIzMywic2l0ZV9zdGF0dXMiOjAsImlhdCI6MTcxNDY0MzI4MX0.QXsuG6PL2rBZP92YZMTeMRnO3at8Sfq2gOf22rG6jeg"
    : req.url;

  const urlObj = new URL(previewUrl, `http://${hostname}`);
  const pid = urlObj.searchParams.get('pid');
  

  if (hostname.startsWith('preview.')) {
    const innerJson = JSON.stringify({
      domain: hostname,
      token: pid
    });

    const payload = {
      data: innerJson
    };

    try {
      const response = await axios.post(`https://${hostname}/api/user/getJbo`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      
      return { props: { products: response.data } };
    } catch (error) {
      // To capture logs
      logError('getJbo', error);
    }
  }

  try {

    const cachedProducts = await redis.get(hostname);

    if (cachedProducts) {
      
      return { props: { products: JSON.parse(cachedProducts) } };
    }

    // If no cache, make an API call for no cached Products
    const innerJson = JSON.stringify({ domain: hostname });
    const payload = {
      data: JSON.stringify({ domain: hostname })
    };

    
    const response = await axios.post(`https://${hostname}/api/user/getJbo`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.data && Object.keys(response.data).length) {
      
      return { props: { products: response.data } };
    }

    // To capture logs
    logError('getJbo', error);
    // Handle the scenario where the API call does not return data
    
    return { props: { products: [] } };

  } catch (error) {
    // To capture logs
    logError('getJbo', error);

    return { props: { products: [] } };
  }
}

export default About